import { markRaw, defineAsyncComponent } from 'vue';
import { store } from '@/store';
import { globalConfig } from '@/configuration/config';

import LoadingComponent from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/ImageLoader.vue';

const FirstFloor = defineAsyncComponent({
  loader: () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/FoxStudios.vue').then(
      (module) => {
        store.dispatch(
          'DigitalTwinStore/addComponentToCachedList',
          'FoxStudios'
        );
        return module.default as Record<string, unknown>;
      }
    ),

  loadingComponent: LoadingComponent,

  delay: 0,
});

// Hotspots
const B32DHotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxB32D.vue')
);
const B32UHotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxB32U.vue')
);
const B26Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxB26.vue')
);
const B99Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxB99.vue')
);
const Stage05Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage5.vue')
);
const Stage06Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage6.vue')
);
const Stage09Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage9.vue')
);
const Stage11Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage11.vue')
);
const Stage15Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage15.vue')
);
const Stage16Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage16.vue')
);
const Stage17Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage17.vue')
);
const Stage19Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage19.vue')
);
const Stage20Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage20.vue')
);
const Stage21Hotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/foxStage21.vue')
);
const GalaxyPkgHotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/GalaxyPkg.vue')
);
const PicoWestPkgHotspot = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/PicoWestPkg.vue')
);
const hiddenLabels = defineAsyncComponent(
  () =>
    import('@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Components/DrawingComponents/FOXStudios_1st_Floor/SuitesHotspots/hiddenLabels.vue')
);

import {
  dialog,
  childTab,
} from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/API/floors';

import { IFoxStudiosFloor } from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/types/FoxStudiosFloor.ts';

const isTruthyDemoFlag = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === 'yes';
  }

  return false;
};

const isDemoMode =
  isTruthyDemoFlag(globalConfig.get().E360_IS_DEMO) ||
  isTruthyDemoFlag(import.meta.env.E360_IS_DEMO);

const demoLabel = (text: string): string => {
  if (!isDemoMode) return text;
  return text
    .replace(/STAGE/g, 'SET')
    .replace(/Stage/g, 'Set')
    .replace(/Galaxy/g, 'Solar A')
    .replace(/Pico[- ]West/g, 'Mountain View');
};

const handleAction = (tab: MouseEvent | KeyboardEvent | number) => {
  if (typeof tab === 'number') {
    dialog.value = true;
    childTab.value = tab;
  }
};

const firstFloor: IFoxStudiosFloor = {
  id: 0,
  name: 'Campus Site',
  loadedName: 'FoxStudios',
  component: markRaw(FirstFloor),
  hotspotsList: [
    // B32D Hotspot
    {
      id: 0,
      name: 'Building 32 Solar',
      equipmentId: 2146,
      uom: 'Solar',
      component: markRaw(B32DHotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: 'Building 32 Solar',
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '62.8%',
        left: '39.48%',
        width: '6.9%',
        height: '6.9%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: '',
    },
    // B32U Hotspot
    {
      id: 1,
      name: 'Building 32 Electricity',
      equipmentId: 2150,
      uom: 'Electricity',
      component: markRaw(B32UHotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: 'Building 32 Electricity',
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '55.7%',
        left: '39.39%',
        width: '7.1%',
        height: '7.1%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: 'B32',
    },
    // B26 Hotspot
    {
      id: 2,
      name: 'Building 26/29',
      equipmentId: 2142,
      uom: 'Electricity',
      component: markRaw(B26Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: 'Building 26/29',
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '71.6%',
        left: '28%',
        width: '13%',
        height: '13%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: 'B26',
    },
    // Stage 5 Hotspot
    {
      id: 3,
      name: demoLabel('Stage 5'),
      equipmentId: 2141,
      uom: 'Electricity',
      component: markRaw(Stage05Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 5'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '71.55%',
        left: '16.58%',
        width: '14.15%',
        height: '14.15%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 5'),
    },
    // Stage 6 Hotspot
    {
      id: 4,
      name: demoLabel('Stage 6'),
      equipmentId: 2140,
      uom: 'Electricity',
      component: markRaw(Stage06Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 6'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '55.4%',
        left: '16.59%',
        width: '14.15%',
        height: '14.15%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 6'),
    },
    // Stage 9 Hotspot
    {
      id: 5,
      name: demoLabel('Stage 9'),
      equipmentId: 2139,
      uom: 'Electricity',
      component: markRaw(Stage09Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 9'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '53.8%',
        left: '46.8%',
        width: '12.6%',
        height: '12.6%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 9'),
    },
    // Stage 20 Hotspot
    {
      id: 6,
      name: demoLabel('Stage 20'),
      equipmentId: 2144,
      uom: 'Electricity',
      component: markRaw(Stage20Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 20'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '38.6%',
        left: '38.8%',
        width: '7.4%',
        height: '7.4%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 20'),
    },
    // Stage 21 Hotspot
    {
      id: 7,
      name: demoLabel('Stage 21'),
      equipmentId: 2143,
      uom: 'Electricity',
      component: markRaw(Stage21Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 21'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '27.7%',
        left: '38.8%',
        width: '7.3%',
        height: '7.3%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 21'),
    },
    // Galaxy Pkg Solar Hotspot
    {
      id: 8,
      name: demoLabel('Galaxy Pkg Solar'),
      equipmentId: 2133,
      uom: 'Electricity',
      component: markRaw(GalaxyPkgHotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Galaxy Pkg Solar'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '25.1%',
        left: '19.5%',
        width: '13.8%',
        height: '13.8%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('Galaxy'),
    },
    // Stage 17 Hotspot
    {
      id: 9,
      name: demoLabel('Stage 17'),
      equipmentId: 2147,
      uom: 'Electricity',
      component: markRaw(Stage17Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 17'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '13.2%',
        left: '47.35%',
        width: '9.1%',
        height: '9.1%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 17'),
    },
    // Stage 19 Hotspot
    {
      id: 10,
      name: demoLabel('Stage 19'),
      equipmentId: 2149,
      uom: 'Electricity',
      component: markRaw(Stage19Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 19'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '12.2%',
        left: '56.6%',
        width: '10.2%',
        height: '10.2%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 19'),
    },
    // Stage 11 Hotspot
    {
      id: 11,
      name: demoLabel('Stage 11'),
      equipmentId: 2138,
      uom: 'Electricity',
      component: markRaw(Stage11Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 11'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '48.7%',
        left: '72.37%',
        width: '8.85%',
        height: '8.85%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 11'),
    },
    // Building 99 Solar Hotspot
    {
      id: 12,
      name: 'Building 99 Solar',
      equipmentId: 2145,
      uom: 'Electricity',
      component: markRaw(B99Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: 'Building 99 Solar',
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '60.2%',
        left: '66.2%',
        width: '14.8%',
        height: '14.8%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: 'B99',
    },
    // Stage 15 Solar Hotspot
    {
      id: 13,
      name: demoLabel('Stage 15 Solar'),
      equipmentId: 2137,
      uom: 'Electricity',
      component: markRaw(Stage15Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 15 Solar'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '59.8%',
        left: '82.15%',
        width: '10.3%',
        height: '10.3%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 15'),
    },
    // Stage 16 Solar Hotspot
    {
      id: 14,
      name: demoLabel('Stage 16 Solar'),
      equipmentId: 2148,
      uom: 'Electricity',
      component: markRaw(Stage16Hotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Stage 16 Solar'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '72%',
        left: '82.15%',
        width: '10.3%',
        height: '10.3%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('STAGE 16'),
    },
    // Pico West Pkg Solar Hotspot
    {
      id: 15,
      name: demoLabel('Pico West Pkg Solar'),
      equipmentId: 2134,
      uom: 'Electricity',
      component: markRaw(PicoWestPkgHotspot),
      techInfo: {
        latestReadings: {
          amps: 0,
          kilowatts: 0,
        },
        location: demoLabel('Pico West Pkg Solar'),
        icon: '',
      },
      style: {
        position: 'absolute',
        top: '84.7%',
        left: '83.3%',
        width: '11.6%',
        height: '11.6%',
        cursor: 'pointer',
      },
      miniMapStyle: {},
      hotSpotAction: (tab: MouseEvent | KeyboardEvent | number) => {
        handleAction(tab);
      },
      chipStyle: {},
      color: '',
      label: demoLabel('Pico-West'),
    },
  ],
  overlaysList: isDemoMode
    ? [
        {
          id: 16,
          component: markRaw(hiddenLabels),
          style: {
            position: 'absolute',
            top: '0.15%',
            left: '2.4%',
            width: '99.5%',
            height: '99.5%',
            cursor: 'default',
            pointerEvents: 'none',
          },
        },
      ]
    : [],
  activeTooltip: null,
};

export default firstFloor;
